import {
  useEffect,
  useState,
  type ComponentProps,
  type ComponentType,
} from 'react';
import type { ChoiceMenu } from '@understory-io/leaf/choice-menu';
import type { ExtendedActionMenu } from '@understory-io/leaf/extended-action-menu';
import type { FilterSheetProps } from '~/ui/filters/filter-sheet';
import { clientLogger } from '~/lib/logger';

/*
 * Menus and sheets that show nothing until they are opened, loaded on demand.
 *
 * A closed ChoiceMenu or ExtendedActionMenu renders nothing, and the filter
 * sheet only exists while it is open. Yet their code (react-aria's focus,
 * overlay and press handling, Leaf's Dialog) was about 39 KiB of the
 * JavaScript the home page loads before it can paint its main image. Each is now
 * fetched when the browser is first idle after hydration, or straight away if
 * it is opened before that, and then rendered as is.
 *
 * Not React.lazy: React holds a Suspense fallback for at least ~300 ms, which
 * made the first open slower than loading the code up front.
 *
 * Import these from this module directly. Re-exporting them from a barrel the
 * page already loads would pull the code back into the first load.
 */

const useChoiceMenu = onDemand(() =>
  import('@understory-io/leaf/choice-menu').then(
    (m) => m.ChoiceMenu as ComponentType<ComponentProps<typeof ChoiceMenu>>
  )
);
const useExtendedActionMenu = onDemand(() =>
  import('@understory-io/leaf/extended-action-menu').then(
    (m) =>
      m.ExtendedActionMenu as ComponentType<
        ComponentProps<typeof ExtendedActionMenu>
      >
  )
);
const useFilterSheet = onDemand(() =>
  import('~/ui/filters/filter-sheet').then(
    (m) => m.FilterSheet as ComponentType<FilterSheetProps>
  )
);

export function DeferredChoiceMenu(props: ComponentProps<typeof ChoiceMenu>) {
  const Menu = useChoiceMenu(Boolean(props.open));
  // eslint-disable-next-line react-hooks/static-components -- the same imported component every render (see onDemand), never one created here
  return Menu ? <Menu {...props} /> : null;
}

export function DeferredExtendedActionMenu(
  props: ComponentProps<typeof ExtendedActionMenu>
) {
  const Menu = useExtendedActionMenu(Boolean(props.open));
  // eslint-disable-next-line react-hooks/static-components -- the same imported component every render (see onDemand), never one created here
  return Menu ? <Menu {...props} /> : null;
}

/** The filter sheet; render it only while it is open. */
export function DeferredFilterSheet(props: FilterSheetProps) {
  const Sheet = useFilterSheet(true);
  // eslint-disable-next-line react-hooks/static-components -- the same imported component every render (see onDemand), never one created here
  return Sheet ? <Sheet {...props} /> : null;
}

/**
 * Starts loading the filter sheet once the browser is idle, for pages that
 * mount it only on demand and so would otherwise fetch it on the first tap.
 */
export function usePreloadFilterSheet() {
  useFilterSheet(false);
}

/**
 * A hook that returns `load`'s component once it has arrived, fetching it
 * when the browser is first idle, or immediately while `needed` is true.
 * The component is fetched once and shared by every caller.
 */
function onDemand<P>(load: () => Promise<ComponentType<P>>) {
  let component: ComponentType<P> | undefined;
  let loading: Promise<ComponentType<P>> | undefined;

  function fetchComponent() {
    loading ??= load().then(
      (loaded) => (component = loaded),
      (error: unknown) => {
        // Let the next open or idle period try again.
        loading = undefined;
        throw error;
      }
    );
    return loading;
  }

  return function useOnDemand(needed: boolean): ComponentType<P> | undefined {
    const [loaded, setLoaded] = useState(() => component);

    useEffect(() => {
      if (loaded) return;
      let mounted = true;
      const fetchAndRender = () => {
        fetchComponent().then(
          (c) => {
            if (mounted) setLoaded(() => c);
          },
          (error: unknown) => {
            clientLogger.warn(error, '[deferred-menus] failed to load');
          }
        );
      };
      const cancel = needed ? undefined : whenIdle(fetchAndRender);
      if (needed) fetchAndRender();
      return () => {
        mounted = false;
        cancel?.();
      };
    }, [loaded, needed]);

    return loaded;
  };
}

function whenIdle(callback: () => void): () => void {
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(callback);
    return () => window.cancelIdleCallback(id);
  }
  // Safari has no requestIdleCallback.
  const id = window.setTimeout(callback, 200);
  return () => window.clearTimeout(id);
}
